import { Box } from "@mui/material";
import {
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";
import { AUTH_CARD_STYLES } from "./authCardStyles";

/** 칸 아래 안내 — 오류(빨강, 칸도 빨간 테두리)와 확인(초록, 예: 사용 가능한 이메일) */
export type AuthFieldMessageTone = "error" | "success";

interface FieldFrameProps {
  label: string;
  /** 칸 아래 안내. 여러 줄이면 줄마다 「•」 를 붙인다 */
  message?: string | readonly string[];
  messageTone?: AuthFieldMessageTone;
}

const messageLines = (message: FieldFrameProps["message"]) =>
  (typeof message === "string" ? [message] : message ?? []).filter(Boolean);

function FieldMessage({
  id,
  lines,
  tone,
}: {
  id: string;
  lines: readonly string[];
  tone: AuthFieldMessageTone;
}) {
  if (!lines.length) return null;
  return (
    <Box
      id={id}
      sx={[
        AUTH_CARD_STYLES.message,
        tone === "success" && AUTH_CARD_STYLES.messageSuccess,
      ]}
    >
      {lines.length === 1
        ? lines[0]
        : lines.map((line) => <div key={line}>• {line}</div>)}
    </Box>
  );
}

/** 칸과 안내를 잇는 속성 — `aria-invalid`(오류일 때만 true), 안내가 있으면 `aria-describedby` */
function useFieldIds(
  id: string | undefined,
  message: FieldFrameProps["message"],
  tone: AuthFieldMessageTone
) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const messageId = `${inputId}-message`;
  const lines = messageLines(message);
  return {
    inputId,
    messageId,
    lines,
    describedBy: lines.length ? messageId : undefined,
    invalid: lines.length > 0 && tone === "error",
  };
}

export interface AuthFieldProps
  extends FieldFrameProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, "children"> {
  ref?: Ref<HTMLInputElement>;
  /** 칸 오른쪽 안에 겹치는 단추(비밀번호 보기) */
  adornment?: ReactNode;
  /** 칸 옆에 붙는 단추(중복확인) — 안내는 칸과 단추 아래에 */
  trailing?: ReactNode;
}

/**
 * 채운 입력 칸(marketing-auth-pages 「입력 칸 구조」): 보이는 이름표와 연결되고, 안내 문구는 칸과 `aria-describedby` 로 잇는다.
 * react-hook-form `register` 결과를 그대로 펼쳐 넣는다(`ref` 포함).
 */
export function AuthField({
  label,
  message,
  messageTone = "error",
  adornment,
  trailing,
  id,
  ref,
  ...inputProps
}: AuthFieldProps) {
  const { inputId, messageId, lines, describedBy, invalid } = useFieldIds(
    id,
    message,
    messageTone
  );
  const input = (
    <Box
      component="input"
      ref={ref}
      id={inputId}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      sx={AUTH_CARD_STYLES.input}
      {...inputProps}
    />
  );
  const control = adornment ? (
    <Box component="span" sx={AUTH_CARD_STYLES.password}>
      {input}
      {adornment}
    </Box>
  ) : (
    input
  );
  return (
    <Box sx={AUTH_CARD_STYLES.field}>
      <Box component="label" htmlFor={inputId} sx={AUTH_CARD_STYLES.label}>
        {label}
      </Box>
      {trailing ? (
        <Box sx={AUTH_CARD_STYLES.inputRow}>
          {control}
          {trailing}
        </Box>
      ) : (
        control
      )}
      <FieldMessage id={messageId} lines={lines} tone={messageTone} />
    </Box>
  );
}

const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M3 3l18 18" />
    <path d="M10.6 5.2A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.1M6.6 6.6C3.7 8.5 2 12 2 12s3.5 7 10 7c1.3 0 2.5-.2 3.6-.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

/** 비밀번호 칸 — 보기 단추의 이름이 지금 상태를 알린다(「비밀번호 보기」 ↔ 「비밀번호 숨기기」) */
export function AuthPasswordField(
  props: Omit<AuthFieldProps, "type" | "adornment" | "trailing">
) {
  const { t } = useTranslation("auth");
  const [visible, setVisible] = useState(false);
  return (
    <AuthField
      {...props}
      type={visible ? "text" : "password"}
      adornment={
        <Box
          component="button"
          type="button"
          aria-label={visible ? t("form.hidePassword") : t("form.showPassword")}
          onClick={() => setVisible((value) => !value)}
          sx={AUTH_CARD_STYLES.eye}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </Box>
      }
    />
  );
}

export interface AuthSelectFieldProps
  extends FieldFrameProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  ref?: Ref<HTMLSelectElement>;
  /** 아직 고르지 않았을 때 보이는 글(고를 수 없는 첫 항목) */
  placeholder: string;
  options: readonly { value: string; label: string }[];
}

/** 선택 칸 — 아직 고르지 않으면 안내 글 색으로 보인다 */
export function AuthSelectField({
  label,
  message,
  messageTone = "error",
  placeholder,
  options,
  id,
  ref,
  onChange,
  defaultValue = "",
  ...selectProps
}: AuthSelectFieldProps) {
  const { inputId, messageId, lines, describedBy, invalid } = useFieldIds(
    id,
    message,
    messageTone
  );
  const [empty, setEmpty] = useState(defaultValue === "");
  return (
    <Box sx={AUTH_CARD_STYLES.field}>
      <Box component="label" htmlFor={inputId} sx={AUTH_CARD_STYLES.label}>
        {label}
      </Box>
      <Box
        component="select"
        ref={ref}
        id={inputId}
        defaultValue={defaultValue}
        data-empty={empty ? "" : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
          setEmpty(event.target.value === "");
          onChange?.(event);
        }}
        sx={AUTH_CARD_STYLES.select}
        {...selectProps}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Box>
      <FieldMessage id={messageId} lines={lines} tone={messageTone} />
    </Box>
  );
}
